/* ─────────────────────────────────────────────────────────────────
   Beyond Bohemian — Origin Globe (interactive)
   Orthographic globe that slowly rotates, highlights BB's sourcing
   countries, and drops glowing pins on each origin.

   Interactions:
     • Drag to spin (pointer / touch) — pauses auto-rotation
     • Scroll / pinch to zoom (after the globe is activated)
     • Hover or tap any pin to reveal the country name
     • Tap a PRIMARY origin to fly in + open a deep-dive card
     • Play/Pause + Reset controls (top-right, shown once active)

   Mount: any element with [data-bb-origin-globe]
   Dependencies: D3 v7 + TopoJSON v3 (loaded on demand from jsDelivr)

   ── PRIMARY ORIGINS ───────────────────────────────────────────────
   The 7 `primary` countries below mirror the dedicated sections on
   /pages/where-our-crystals-come-from (the origins that make up the
   majority of our catalog). `region` + `knownFor` + `note` are pulled
   from that page; each card deep-links to the country's #anchor.
   ───────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // ═══ CONFIG ═══════════════════════════════════════════════════
  const ROTATION_DEG_PER_SEC = 6;      // set to 0 for a static globe
  const GLOBE_SIZE           = 640;    // SVG viewBox size
  const MIN_ZOOM             = 1;      // multiples of base scale
  const MAX_ZOOM             = 3.2;
  const SOURCE_URL           = '/pages/where-our-crystals-come-from';
  const ATLAS_URL            = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
  const D3_URL               = 'https://cdn.jsdelivr.net/npm/d3@7/dist/d3.min.js';
  const TOPOJSON_URL         = 'https://cdn.jsdelivr.net/npm/topojson-client@3/dist/topojson-client.min.js';

  // 31 sourcing origins. Count is current + historical catalog.
  // The 7 with primary:true are the majority-of-catalog origins that
  // have dedicated sections on the sourcing page.
  const BB_SOURCING_COUNTRIES = [
    { name: 'Angola',                   lat: -11.2, lng: 17.9  },
    { name: 'Argentina',                lat: -38.4, lng: -63.6 },
    { name: 'Australia',                lat: -25.3, lng: 133.8 },
    { name: 'Austria',                  lat: 47.5,  lng: 14.6  },
    { name: 'Bolivia',                  lat: -16.3, lng: -63.6 },
    { name: 'Botswana',                 lat: -22.3, lng: 24.7  },
    { name: 'Brazil',                   lat: -14.2, lng: -51.9,
      primary: true, anchor: 'brazil',
      region: 'Minas Gerais · Bahia · Rio Grande do Sul',
      knownFor: ['Quartz', 'Beryl', 'Citrine'],
      note: 'Our largest single origin and the working heart of the global mineral trade, spanning three geological provinces.' },
    { name: 'Canada',                   lat: 56.1,  lng: -106.3},
    { name: 'China',                    lat: 35.8,  lng: 104.2 },
    { name: 'Colombia',                 lat: 4.6,   lng: -74.3 },
    { name: 'Dem. Rep. Congo',          lat: -4.0,  lng: 21.8  },
    { name: 'Dominican Rep.',           lat: 18.7,  lng: -70.2 },
    { name: 'eSwatini',                 lat: -26.5, lng: 31.5  },
    { name: 'India',                    lat: 20.6,  lng: 78.9  },
    { name: 'Indonesia',                lat: -0.8,  lng: 113.9 },
    { name: 'Madagascar',               lat: -18.8, lng: 47.5,
      primary: true, anchor: 'madagascar',
      region: 'Maniry · Toliara · Antsirabe',
      knownFor: ['Labradorite', 'Jasper', 'Citrine'],
      note: 'A geologically singular island, mined largely through cooperatives and small-scale diggers.' },
    { name: 'Malawi',                   lat: -13.3, lng: 34.3  },
    { name: 'Mexico',                   lat: 23.6,  lng: -102.5,
      primary: true, anchor: 'mexico',
      region: 'Chihuahua · Jalisco · Estado de México',
      knownFor: ['Obsidian', 'Lace Agate'],
      note: 'Volcanic heritage across central and northern states, known for obsidian and lace agate.' },
    { name: 'Mozambique',               lat: -18.7, lng: 35.5  },
    { name: 'Namibia',                  lat: -22.9, lng: 18.5  },
    { name: 'Norway',                   lat: 60.5,  lng: 8.5   },
    { name: 'Peru',                     lat: -9.2,  lng: -75.0,
      primary: true, anchor: 'peru',
      region: 'Andes · Lima · Junín · Arequipa',
      knownFor: ['Calcite', 'Opal', 'Lepidolite'],
      note: 'High-altitude Andean deposits producing calcite, opal, and lepidolite.' },
    { name: 'Congo',                    lat: -0.2,  lng: 15.8  },
    { name: 'Russia',                   lat: 61.5,  lng: 105.3 },
    { name: 'South Africa',             lat: -30.6, lng: 22.9,
      primary: true, anchor: 'south-africa',
      region: 'Northern Cape · Magaliesberg · Western Cape',
      knownFor: ['Tiger Eye', 'Cactus Quartz', 'Unakite'],
      note: 'Some of Earth’s most ancient rock, source of tiger eye, cactus quartz, and unakite.' },
    { name: 'Tanzania',                 lat: -6.4,  lng: 34.9  },
    { name: 'United Kingdom',           lat: 55.4,  lng: -3.4  },
    { name: 'United States of America', lat: 37.1,  lng: -95.7,
      primary: true, anchor: 'united-states',
      region: 'Arizona · New Mexico',
      knownFor: ['Peridot', 'Obsidian', 'Selenite'],
      note: 'Short, verifiable supply chains close to home, with conditions we can confirm firsthand.' },
    { name: 'Uruguay',                  lat: -32.5, lng: -55.8,
      primary: true, anchor: 'uruguay',
      region: 'Artigas Department',
      knownFor: ['Amethyst'],
      note: 'A premium specimen origin defined by one stone: deep-color amethyst geodes from Artigas.' },
    { name: 'Zambia',                   lat: -13.1, lng: 27.8  },
    { name: 'Zimbabwe',                 lat: -19.0, lng: 29.2  },
  ];

  // Friendly display names (atlas names can be terse).
  const BB_DISPLAY_NAME = {
    'Dem. Rep. Congo':            'Democratic Republic of the Congo',
    'Congo':                      'Republic of the Congo',
    'Dominican Rep.':             'Dominican Republic',
    'United States of America':   'United States',
  };

  // Natural Earth atlas name variants — match regardless of atlas version.
  const BB_COUNTRY_ALIASES = {
    'Dem. Rep. Congo':            ['Democratic Republic of the Congo', 'Dem. Rep. Congo', 'DR Congo'],
    'Congo':                      ['Republic of the Congo', 'Congo'],
    'Dominican Rep.':             ['Dominican Republic', 'Dominican Rep.'],
    'eSwatini':                   ['eSwatini', 'Eswatini', 'Swaziland'],
    'United States of America':   ['United States of America', 'United States', 'USA'],
    'United Kingdom':             ['United Kingdom', 'UK', 'Great Britain'],
  };
  const PRIMARY_NAMES = new Set(BB_SOURCING_COUNTRIES.filter(c => c.primary).map(c => c.name));

  // ═══ LOADER ═══════════════════════════════════════════════════
  const scriptPromises = {};
  const ensureScript = (src, globalKey) => {
    if (window[globalKey]) return Promise.resolve();
    if (scriptPromises[src]) return scriptPromises[src];
    return scriptPromises[src] = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load ' + src));
      document.head.appendChild(s);
    });
  };

  let atlasPromise = null;
  const loadAtlas = async () => {
    if (atlasPromise) return atlasPromise;
    atlasPromise = (async () => {
      await ensureScript(TOPOJSON_URL, 'topojson');
      await ensureScript(D3_URL, 'd3');
      const res = await fetch(ATLAS_URL);
      const data = await res.json();
      return window.topojson.feature(data, data.objects.countries);
    })();
    return atlasPromise;
  };

  // ═══ HELPERS ══════════════════════════════════════════════════
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const el = (name, attrs) => {
    const node = document.createElementNS(SVG_NS, name);
    if (attrs) { for (const k in attrs) { if (attrs[k] != null) node.setAttribute(k, attrs[k]); } }
    return node;
  };
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const displayName = (c) => BB_DISPLAY_NAME[c.name] || c.name;

  const buildSourcingSet = (topo) => {
    const aliasToCanonical = new Map();
    BB_SOURCING_COUNTRIES.forEach(c => {
      const aliases = BB_COUNTRY_ALIASES[c.name] || [c.name];
      aliases.forEach(a => aliasToCanonical.set(a, c.name));
    });
    const matched = new Map(); // atlasName -> canonical
    const unmatched = new Set(BB_SOURCING_COUNTRIES.map(c => c.name));
    topo.features.forEach(f => {
      const canon = aliasToCanonical.get(f.properties.name);
      if (canon) { matched.set(f.properties.name, canon); unmatched.delete(canon); }
    });
    if (unmatched.size) console.warn('[bb-origin-globe] Countries not matched in atlas:', [...unmatched]);
    return matched;
  };

  // ═══ RENDER ═══════════════════════════════════════════════════
  const renderGlobe = (mount, topo) => {
    const W = GLOBE_SIZE, H = GLOBE_SIZE;
    const d3 = window.d3;
    const wrap = mount.closest('.bb-origin-globe-wrap') || mount.parentElement;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const baseScale = W / 2 - 24;
    let lambda = -30, beta = -18, zoom = 1;

    const projection = d3.geoOrthographic()
      .scale(baseScale).translate([W / 2, H / 2]).rotate([lambda, beta, 0]);
    const pathGen   = d3.geoPath(projection);
    const graticule = d3.geoGraticule10();
    const matchedMap = buildSourcingSet(topo);

    // ── Build SVG ──
    mount.innerHTML = '';
    const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, xmlns: SVG_NS, role: 'presentation' });

    const defs = el('defs');
    defs.innerHTML = `
      <radialGradient id="bbGlobeFace" cx="30%" cy="30%">
        <stop offset="0%"   stop-color="#1E3A40" stop-opacity="1"/>
        <stop offset="60%"  stop-color="#122A2E" stop-opacity="1"/>
        <stop offset="100%" stop-color="#06181A" stop-opacity="1"/>
      </radialGradient>
      <radialGradient id="bbAuroraPin" cx="50%" cy="50%">
        <stop offset="0%"   stop-color="#FFF3C8" stop-opacity="1"/>
        <stop offset="50%"  stop-color="#C9A14A" stop-opacity=".6"/>
        <stop offset="100%" stop-color="#C9A14A" stop-opacity="0"/>
      </radialGradient>
      <filter id="bbGlobeGlow"><feGaussianBlur stdDeviation="2"/></filter>
    `;
    svg.appendChild(defs);
    svg.appendChild(el('circle', { cx: W / 2, cy: H / 2, r: baseScale, fill: 'url(#bbGlobeFace)' }));

    const graticulePath = el('path', { fill: 'none', stroke: '#5E8A8F', 'stroke-width': 0.4, opacity: 0.22 });
    svg.appendChild(graticulePath);

    const countryGroup = el('g');
    const countryNodes = topo.features.map(f => {
      const canon = matchedMap.get(f.properties.name);
      const isSource = !!canon;
      const isPrimary = isSource && PRIMARY_NAMES.has(canon);
      const node = el('path', {
        fill:   isPrimary ? 'rgba(201,161,74,.34)' : isSource ? 'rgba(201,161,74,.2)' : 'rgba(180,210,215,.08)',
        stroke: isSource ? 'rgba(201,161,74,.65)' : 'rgba(180,210,215,.26)',
        'stroke-width': isPrimary ? 0.9 : isSource ? 0.6 : 0.4,
      });
      countryGroup.appendChild(node);
      return { node, feature: f };
    });
    svg.appendChild(countryGroup);

    svg.appendChild(el('circle', {
      cx: W / 2, cy: H / 2, r: baseScale, fill: 'none',
      stroke: 'rgba(170,215,220,.4)', 'stroke-width': 0.8,
    }));

    // ── pins ──
    const pinGroup = el('g');
    const pinNodes = BB_SOURCING_COUNTRIES.map(c => {
      const isPrimary = !!c.primary;
      const g = el('g', { style: 'cursor:pointer' });
      if (isPrimary) g.setAttribute('class', 'bb-origin-pin is-primary');
      let ring = null;
      if (isPrimary) {
        ring = el('circle', { r: 6, fill: 'none', stroke: '#C9A14A', 'stroke-width': 1.1, class: 'bb-origin-pin-ring' });
        g.appendChild(ring);
      }
      const halo = el('circle', { r: isPrimary ? 12 : 9, fill: 'url(#bbAuroraPin)', filter: 'url(#bbGlobeGlow)' });
      const dot  = el('circle', { r: isPrimary ? 2.6 : 1.8, fill: '#FFF3C8' });
      const hit  = el('circle', { r: isPrimary ? 15 : 13, fill: 'transparent' });
      g.appendChild(halo); g.appendChild(dot); g.appendChild(hit);
      pinGroup.appendChild(g);
      const pin = { g, ring, halo, dot, hit, country: c, primary: isPrimary };
      hit.addEventListener('mouseenter', () => { hoverPin = pin; if (!selectedPin) showTip(pin); });
      hit.addEventListener('mouseleave', () => { if (hoverPin === pin) hoverPin = null; if (!selectedPin) hideTip(); });
      hit.addEventListener('pointerdown', (e) => { e.stopPropagation(); });
      hit.addEventListener('click', (e) => { e.stopPropagation(); selectPin(pin); });
      return pin;
    });
    svg.appendChild(pinGroup);
    mount.appendChild(svg);

    // ── HTML overlay UI ──
    const tip = document.createElement('div');
    tip.className = 'bb-origin-tip';
    tip.innerHTML = '<div class="bb-origin-tip-name"></div><div class="bb-origin-tip-sub">Sourcing origin</div>';
    const tipName = tip.querySelector('.bb-origin-tip-name');
    wrap.appendChild(tip);

    const card = document.createElement('div');
    card.className = 'bb-origin-card';
    card.innerHTML =
      '<button type="button" class="bb-origin-card-close" aria-label="Close">&times;</button>' +
      '<div class="bb-origin-card-eyebrow">Primary Origin</div>' +
      '<div class="bb-origin-card-name"></div>' +
      '<div class="bb-origin-card-region"></div>' +
      '<div class="bb-origin-card-chips"></div>' +
      '<p class="bb-origin-card-note"></p>' +
      '<a class="bb-origin-card-link" href="' + SOURCE_URL + '">See how we source <span aria-hidden="true">&rarr;</span></a>';
    const cardName   = card.querySelector('.bb-origin-card-name');
    const cardRegion = card.querySelector('.bb-origin-card-region');
    const cardChips  = card.querySelector('.bb-origin-card-chips');
    const cardNote   = card.querySelector('.bb-origin-card-note');
    const cardLink   = card.querySelector('.bb-origin-card-link');
    wrap.appendChild(card);
    card.querySelector('.bb-origin-card-close').addEventListener('click', (e) => { e.stopPropagation(); clearSelection(); });

    const hint = document.createElement('div');
    hint.className = 'bb-origin-hint';
    hint.innerHTML = '<span class="bb-origin-hint-full">Drag to spin · Scroll to zoom · Tap an origin</span>' +
                     '<span class="bb-origin-hint-mini">Tap an origin to explore</span>';
    wrap.appendChild(hint);

    const controls = document.createElement('div');
    controls.className = 'bb-origin-controls';
    const playBtn = document.createElement('button');
    playBtn.type = 'button'; playBtn.className = 'bb-origin-ctrl';
    playBtn.setAttribute('aria-label', 'Play or pause rotation'); playBtn.innerHTML = '&#9658;';
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button'; resetBtn.className = 'bb-origin-ctrl';
    resetBtn.setAttribute('aria-label', 'Reset view'); resetBtn.innerHTML = '&#8635;';
    controls.appendChild(playBtn); controls.appendChild(resetBtn);
    wrap.appendChild(controls);

    // ── state ──
    let spinning = !(reducedMotion || ROTATION_DEG_PER_SEC === 0);
    let activated = false, hoverPin = null, selectedPin = null, rafId = null, tweenId = null;
    const setActive = () => { if (!activated) { activated = true; wrap.classList.add('is-active'); } };

    // ── per-frame update ──
    const update = () => {
      projection.scale(baseScale * zoom).rotate([lambda, beta, 0]);
      graticulePath.setAttribute('d', pathGen(graticule) || '');
      countryNodes.forEach(({ node, feature }) => {
        const d = pathGen(feature);
        if (d) node.setAttribute('d', d); else node.removeAttribute('d');
      });
      const centerLon = -lambda, centerLat = -beta;
      pinNodes.forEach((pin) => {
        const { g, halo, dot, hit, ring, country } = pin;
        const pt = projection([country.lng, country.lat]);
        const far = d3.geoDistance([country.lng, country.lat], [centerLon, centerLat]) > Math.PI / 2 - 0.02;
        if (!pt || isNaN(pt[0]) || far) {
          g.style.display = 'none';
          if (pin === selectedPin && !pin.primary) hideTip();
          return;
        }
        g.style.display = '';
        halo.setAttribute('cx', pt[0]); halo.setAttribute('cy', pt[1]);
        dot.setAttribute('cx', pt[0]);  dot.setAttribute('cy', pt[1]);
        hit.setAttribute('cx', pt[0]);  hit.setAttribute('cy', pt[1]);
        if (ring) { ring.setAttribute('cx', pt[0]); ring.setAttribute('cy', pt[1]); }
        if ((pin === selectedPin && !pin.primary) || pin === hoverPin) positionTip(pin, pt);
      });
    };

    // ── tooltip ──
    const toLocal = (pt) => {
      const rect = mount.getBoundingClientRect();
      return [ (pt[0] / W) * rect.width, (pt[1] / H) * rect.height ];
    };
    const positionTip = (pin, ptOverride) => {
      const pt = ptOverride || projection([pin.country.lng, pin.country.lat]);
      if (!pt) { tip.classList.remove('is-visible'); return; }
      const [x, y] = toLocal(pt);
      tip.style.left = x + 'px'; tip.style.top = y + 'px';
    };
    const showTip = (pin) => { tipName.textContent = displayName(pin.country); positionTip(pin); tip.classList.add('is-visible'); };
    const hideTip = () => { tip.classList.remove('is-visible'); };

    // ── deep-dive card ──
    const showCard = (pin) => {
      const c = pin.country;
      cardName.textContent = displayName(c);
      cardRegion.textContent = c.region || '';
      cardRegion.style.display = c.region ? '' : 'none';
      cardChips.innerHTML = '';
      (c.knownFor || []).forEach(k => {
        const chip = document.createElement('span');
        chip.className = 'bb-origin-chip'; chip.textContent = k;
        cardChips.appendChild(chip);
      });
      cardNote.textContent = c.note || '';
      cardLink.setAttribute('href', SOURCE_URL + (c.anchor ? '#' + c.anchor : ''));
      card.classList.add('is-visible');
    };
    const hideCard = () => { card.classList.remove('is-visible'); };

    // ── animated tween to a target view ──
    const tweenTo = (targetLambda, targetBeta, targetZoom, done) => {
      if (tweenId) cancelAnimationFrame(tweenId);
      const sL = lambda, sB = beta, sZ = zoom;
      let dL = ((targetLambda - sL + 540) % 360) - 180;
      const dB = targetBeta - sB, dZ = targetZoom - sZ;
      const t0 = performance.now(), dur = 620;
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const step = (now) => {
        const t = Math.min(1, (now - t0) / dur), k = ease(t);
        lambda = sL + dL * k; beta = sB + dB * k; zoom = sZ + dZ * k;
        update();
        if (t < 1) tweenId = requestAnimationFrame(step);
        else { tweenId = null; if (done) done(); }
      };
      tweenId = requestAnimationFrame(step);
    };

    const selectPin = (pin) => {
      setActive();
      spinning = false; playBtn.innerHTML = '&#9658;';
      selectedPin = pin;
      if (pin.primary) { hideTip(); showCard(pin); }
      else { hideCard(); showTip(pin); }
      tweenTo(-pin.country.lng, -pin.country.lat, Math.max(zoom, 1.7), () => {
        if (pin.primary) showCard(pin); else showTip(pin);
      });
    };
    const clearSelection = () => { selectedPin = null; hideCard(); if (!hoverPin) hideTip(); };

    // ── drag to spin ──
    let dragging = false, lastX = 0, lastY = 0, moved = 0;
    const dragSensitivity = () => 0.32 / zoom;
    mount.addEventListener('pointerdown', (e) => {
      setActive();
      dragging = true; moved = 0; lastX = e.clientX; lastY = e.clientY;
      spinning = false; playBtn.innerHTML = '&#9658;';
      mount.classList.add('is-dragging');
      mount.setPointerCapture && mount.setPointerCapture(e.pointerId);
    });
    mount.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      moved += Math.abs(dx) + Math.abs(dy);
      const k = dragSensitivity();
      lambda += dx * k; beta = clamp(beta - dy * k, -80, 80);
      update();
    });
    const endDrag = () => {
      if (!dragging) return;
      dragging = false; mount.classList.remove('is-dragging');
      if (moved < 4 && selectedPin) clearSelection();
    };
    mount.addEventListener('pointerup', endDrag);
    mount.addEventListener('pointercancel', endDrag);
    mount.addEventListener('pointerleave', () => { if (!selectedPin && !dragging) hideTip(); });

    // ── wheel / pinch zoom (only once activated) ──
    mount.addEventListener('wheel', (e) => {
      if (!activated) return;
      e.preventDefault();
      zoom = clamp(zoom * (1 - e.deltaY * 0.0012), MIN_ZOOM, MAX_ZOOM);
      update();
    }, { passive: false });

    // ── controls ──
    playBtn.addEventListener('click', () => {
      spinning = !spinning;
      playBtn.innerHTML = spinning ? '&#10073;&#10073;' : '&#9658;';
      if (spinning) clearSelection();
    });
    resetBtn.addEventListener('click', () => {
      clearSelection();
      tweenTo(-30, -18, 1, () => { spinning = true; playBtn.innerHTML = '&#10073;&#10073;'; });
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && selectedPin) clearSelection(); });

    update();

    // ── auto-rotation loop ──
    if (reducedMotion || ROTATION_DEG_PER_SEC === 0) return;
    let lastT = performance.now();
    const tick = (t) => {
      const dt = (t - lastT) / 1000; lastT = t;
      if (spinning && !dragging && !tweenId) { lambda += ROTATION_DEG_PER_SEC * dt; update(); }
      rafId = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { if (!rafId) { lastT = performance.now(); rafId = requestAnimationFrame(tick); } }
        else if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      });
    }, { threshold: 0.01 });
    io.observe(mount);
  };

  // ═══ BOOT ═════════════════════════════════════════════════════
  const init = async (mount) => {
    if (mount.dataset.bbOriginInit === '1') return;
    mount.dataset.bbOriginInit = '1';
    try { renderGlobe(mount, await loadAtlas()); }
    catch (e) { console.error('[bb-origin-globe] Failed to initialize', e); }
  };
  const initAll = () => { document.querySelectorAll('[data-bb-origin-globe]').forEach(init); };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
